import type {
  PaginatedResponse,
  PaginationMeta,
} from '~/types/PaginatedResponse'
import { useAuthStore } from '~/stores/auth'

type ParamableType = string | number | boolean | undefined

export interface UsePaginationOptions {
  immediate?: boolean
  initialPage?: number
  initialLimit?: number
  params?: MaybeRefOrGetter<Record<string, ParamableType>>
}

export function usePagination<T, M extends PaginationMeta = PaginationMeta>(
  baseUrl: MaybeRefOrGetter<string | undefined | null>,
  options?: UsePaginationOptions,
) {
  const authStore = useAuthStore()

  const {
    immediate = true,
    initialPage = 1,
    initialLimit = 10,
    params = {},
  } = options ?? {}

  const config = useRuntimeConfig()

  const page = ref(initialPage)
  const limit = ref(initialLimit)

  const computedParams = computed(() => {
    return {
      page: page.value,
      limit: limit.value,
      ...toValue(params),
    }
  })

  const url = computed(() => {
    if (!toValue(baseUrl)) {
      return undefined
    }

    return `${config.public.serverUrl}${toValue(baseUrl)}`
  })

  const useFetchResponse = useFetch<PaginatedResponse<T, M>>(
    url as MaybeRefOrGetter<string>, // type case to deal with TS
    {
      query: computedParams,
      immediate,
      baseURL: config.public.serverUrl,
      onRequest: async ({ request, options }) => {
        if (!request) {
          throw new Error('Undefined request') // this error never surfaces, it just stops the request from being made when url is undefined
        }

        const token = await authStore.getToken()
        if (!token) {
          throw new Error('Unable to get access token.')
        }

        options.headers.set('Authorization', `Bearer ${token}`)
      },
    },
  )

  const data = computed(() => useFetchResponse.data.value?.data ?? [])
  const meta = computed(() => useFetchResponse.data.value?.meta ?? undefined)
  const error = computed(() => useFetchResponse.error.value ?? null)

  const status = computed(() => useFetchResponse.status.value)
  const isLoading = computed(() => status.value === 'pending')

  const totalItems = computed(() => meta.value?.totalItems ?? 0)

  async function refresh() {
    await useFetchResponse.refresh()
  }

  return {
    data,
    meta,
    error,
    status,
    isLoading,
    totalItems,
    page,
    limit,
    refresh,
  }
}
